import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Shield,
  Lock,
  Camera,
  FolderLock,
  Cpu,
  Sparkles,
  Layers,
  ArrowLeft,
  Calendar,
  RefreshCw,
  Mail,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Printer,
  Copy,
  Check,
  HardDrive,
  EyeOff,
  WifiOff,
  UserCheck,
  ShieldCheck,
  FolderCheck,
} from "lucide-react";

export default function DocumentScanner() {
  const [copied, setCopied] = useState(false);

  const sections = [
    { id: "information-collection", title: "1. Information Collection & Use" },
    { id: "device-permissions", title: "2. Device Permissions" },
    { id: "on-device-technologies", title: "3. On-Device Technologies" },
    { id: "data-storage-security", title: "4. Data Storage & Security" },
    { id: "childrens-privacy", title: "5. Children's Privacy" },
    { id: "third-party-links", title: "6. Third-Party Links" },
    { id: "policy-changes", title: "7. Changes to This Policy" },
    { id: "contact-us", title: "8. Contact Information" },
  ];

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("jkjitendra4292@gmail.com");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased selection:bg-teal-600 selection:text-white print:bg-white print:text-black">
      {/* Top sticky navigation bar */}
      <nav className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md print:hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-teal-700"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
                <FileText className="h-4 w-4" />
              </span>
              <span className="hidden text-sm font-bold text-slate-900 sm:inline">
                Document Scanner
              </span>
            </div>

            <button
              onClick={handlePrint}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
              title="Print or Save as PDF"
            >
              <Printer className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Print / Save PDF</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 py-16 text-white shadow-xl print:bg-none print:py-6 print:text-black print:shadow-none">
        <div className="pointer-events-none absolute inset-0 opacity-15 print:hidden">
          <div className="absolute -left-12 -top-12 h-64 w-64 rounded-full bg-teal-500 blur-3xl"></div>
          <div className="absolute right-0 top-1/2 h-80 w-80 rounded-full bg-emerald-500 blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-teal-300 print:text-slate-700">
            <ShieldCheck className="h-4 w-4 text-teal-400" />
            Official Privacy Documentation
          </div>

          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl print:text-3xl">
            Privacy Policy
          </h1>
          <p className="mt-2 text-lg font-medium text-teal-200 sm:text-xl print:text-slate-700">
            Document Scanner
          </p>

          {/* Dates metadata pills */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/80 px-3.5 py-1.5 text-slate-300 print:border-slate-300 print:bg-slate-100 print:text-slate-800">
              <Calendar className="h-4 w-4 text-teal-400" />
              <strong>Effective Date:</strong> October 4, 2026
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/80 px-3.5 py-1.5 text-slate-300 print:border-slate-300 print:bg-slate-100 print:text-slate-800">
              <RefreshCw className="h-4 w-4 text-emerald-400" />
              <strong>Last Updated:</strong> October 4, 2026
            </span>
          </div>
        </div>
      </header>

      {/* Main Content Layout */}
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 print:py-4">
        {/* Intro Card */}
        <section className="mb-8 overflow-hidden rounded-2xl border border-teal-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="space-y-4 leading-relaxed text-slate-700">
            <p className="text-base sm:text-lg">
              Thank you for choosing <strong>Document Scanner</strong> (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). Your privacy is our top priority. Document Scanner is designed from the ground up as a <strong>100% offline, privacy-first utility application</strong>. All document processing, scanning, text extraction (OCR), and file manipulations are performed strictly on your local device.
            </p>
            <p className="text-slate-600">
              This Privacy Policy explains how our application handles your information, what permissions are required, and how your data is protected.
            </p>
          </div>

          {/* Core Privacy Pillars Grid */}
          <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex items-center gap-3 rounded-xl bg-teal-50/70 p-3.5 border border-teal-100">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
                <WifiOff className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-bold text-slate-900">100% Offline</p>
                <p className="text-slate-600">No cloud processing</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-emerald-50/70 p-3.5 border border-emerald-100">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm">
                <EyeOff className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-bold text-slate-900">Zero Tracking</p>
                <p className="text-slate-600">No analytics or ad IDs</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-blue-50/70 p-3.5 border border-blue-100">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
                <UserCheck className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-bold text-slate-900">No Account</p>
                <p className="text-slate-600">No registration or login</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-indigo-50/70 p-3.5 border border-indigo-100">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
                <Lock className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-bold text-slate-900">Local Sandbox</p>
                <p className="text-slate-600">Files stay on hardware</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Jump Table of Contents */}
        <section className="mb-10 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm print:hidden">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500">
            <Layers className="h-4 w-4 text-teal-600" /> Table of Contents
          </h2>
          <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
            {sections.map((sec) => (
              <a
                key={sec.id}
                href={`#${sec.id}`}
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium text-slate-700 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
              >
                {sec.title}
              </a>
            ))}
          </div>
        </section>

        {/* Detailed Policy Sections */}
        <div className="space-y-6">
          {/* Section 1: Information Collection and Use */}
          <article
            id="information-collection"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                1
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Information Collection and Use
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <div className="rounded-xl border border-teal-200 bg-teal-50/70 p-4.5 text-teal-950">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0" />
                  <h3 className="font-bold text-teal-950 text-base">
                    No Personal Data Collection
                  </h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-teal-900 font-medium">
                  Document Scanner <strong>does not collect, store, transmit, or share any personal information, documents, photos, or biometric data</strong> with external servers or third parties.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    No Account Required
                  </div>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                    You do not need to register, create an account, or log in to use Document Scanner.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    No Cloud Uploads
                  </div>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                    Your scanned documents, imported PDFs, photos, signatures, and extracted text never leave your device.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    No Tracking or Analytics
                  </div>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                    We do not use third-party analytics trackers, advertising identifiers, or user telemetry to monitor your activities.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Section 2: Device Permissions */}
          <article
            id="device-permissions"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                2
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Device Permissions and How They Are Used
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Document Scanner only requests the permissions strictly necessary to perform core features:
              </p>

              {/* Responsive Permission Table */}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 bg-slate-100 text-xs font-bold uppercase tracking-wider text-slate-700">
                    <tr>
                      <th className="px-4 py-3 sm:px-6">Permission</th>
                      <th className="px-4 py-3 sm:px-6">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                    <tr className="transition hover:bg-slate-50/70">
                      <td className="whitespace-normal px-4 py-4 font-semibold text-slate-900 align-top sm:px-6 sm:w-1/3">
                        <div className="flex items-start gap-2">
                          <Camera className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="block font-bold">Camera</span>
                            <code className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              android.permission.CAMERA
                            </code>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 leading-relaxed sm:px-6">
                        Used exclusively to capture physical documents, receipts, or notes using the document scanner feature. Images captured by the camera are processed immediately on-device and are never transmitted over the internet.
                      </td>
                    </tr>
                    <tr className="transition hover:bg-slate-50/70">
                      <td className="whitespace-normal px-4 py-4 font-semibold text-slate-900 align-top sm:px-6 sm:w-1/3">
                        <div className="flex items-start gap-2">
                          <HardDrive className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="block font-bold">Photos / Media / Storage</span>
                            <div className="flex flex-col gap-1 mt-1 text-xs text-slate-500">
                              <code className="bg-slate-100 px-1.5 py-0.5 rounded w-fit">
                                READ_EXTERNAL_STORAGE
                              </code>
                              <code className="bg-slate-100 px-1.5 py-0.5 rounded w-fit">
                                WRITE_EXTERNAL_STORAGE
                              </code>
                              <code className="bg-slate-100 px-1.5 py-0.5 rounded w-fit">
                                READ_MEDIA_IMAGES
                              </code>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 leading-relaxed sm:px-6">
                        Used solely to allow you to select existing documents or photos from your gallery for PDF creation/editing, and to save exported PDFs, images, or extracted text directly to your device storage.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">Manage Permissions:</span> You can grant or revoke these permissions at any time through your Android device settings (<strong>Settings &gt; Apps &gt; Document Scanner &gt; Permissions</strong>).
              </div>
            </div>
          </article>

          {/* Section 3: On-Device Technologies */}
          <article
            id="on-device-technologies"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                3
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                On-Device Technologies and Third-Party Services
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                To provide fast, reliable, and high-quality document scanning and recognition without compromising privacy, the app integrates the following on-device frameworks:
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-white">
                      <Cpu className="h-5 w-5" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-base">
                      Google Play Services ML Kit
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                    We utilize Google ML Kit for on-device document boundary detection, perspective correction, image enhancement, and text recognition (OCR). This processing is executed entirely on your hardware via Google Play Services and does not send your documents or scanned images to Google servers for analysis.
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-base">
                      Apache PDFBox (Android)
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                    Used for local PDF creation, merging, splitting, compression, page organization, and password protection without cloud dependencies.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Section 4: Data Storage and Security */}
          <article
            id="data-storage-security"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                4
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Data Storage and Security
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold">
                    <FolderLock className="h-5 w-5 text-teal-600" />
                    Local Storage
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    All files created or imported within Document Scanner are stored in your device&apos;s secure app-specific sandbox directory or public storage locations you explicitly choose (such as your <code className="bg-slate-200 px-1 py-0.5 rounded text-xs">Downloads</code> or <code className="bg-slate-200 px-1 py-0.5 rounded text-xs">Documents</code> folder).
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold">
                    <FolderCheck className="h-5 w-5 text-teal-600" />
                    Data Retention &amp; Deletion
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    You retain complete control over your files. Deleting a document or clearing the app&apos;s data from your device settings will permanently remove the associated files. We have no access to your files and cannot recover deleted documents.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Section 5: Children's Privacy */}
          <article
            id="childrens-privacy"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                5
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Children&apos;s Privacy
              </h2>
            </div>

            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Document Scanner does not target or knowingly collect any personal information from children under the age of 13 (or under 16 in certain jurisdictions).
              </p>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4 text-emerald-950 font-medium text-sm">
                Because the app operates entirely offline and does not collect personal data, it complies with the Children&apos;s Online Privacy Protection Act (COPPA) and the General Data Protection Regulation (GDPR).
              </div>
            </div>
          </article>

          {/* Section 6: Third-Party Links */}
          <article
            id="third-party-links"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                6
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Third-Party Links
              </h2>
            </div>

            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Document Scanner does not contain third-party advertising banners, sponsored tracking links, or external data brokers.
              </p>
              <p>
                If the app displays an external link (such as sending an email to support or opening system sharing sheets), any information shared through third-party apps (e.g., your email client or messaging apps) is governed by the respective third-party&apos;s privacy policy.
              </p>
            </div>
          </article>

          {/* Section 7: Changes to This Privacy Policy */}
          <article
            id="policy-changes"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                7
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Changes to This Privacy Policy
              </h2>
            </div>

            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                We may update our Privacy Policy periodically to reflect new features or legal requirements. Any modifications will be updated on this page with a revised &ldquo;Last Updated&rdquo; date.
              </p>
              <p>
                We recommend checking this policy occasionally for any updates.
              </p>
            </div>
          </article>

          {/* Section 8: Contact Us */}
          <article
            id="contact-us"
            className="scroll-mt-20 rounded-2xl border-2 border-teal-200 bg-gradient-to-br from-teal-50/70 via-white to-slate-50 p-6 shadow-sm sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-teal-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-sm font-bold text-white shadow-sm">
                8
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Contact Us
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                If you have any questions, feedback, or concerns regarding this Privacy Policy or Document Scanner&apos;s privacy practices, please contact us:
              </p>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Email Card */}
                <div className="rounded-xl border border-teal-200/80 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Email Support
                        </p>
                        <a
                          href="mailto:jkjitendra4292@gmail.com"
                          className="font-semibold text-teal-700 hover:underline break-all"
                        >
                          jkjitendra4292@gmail.com
                        </a>
                      </div>
                    </div>

                    <button
                      onClick={handleCopyEmail}
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                      title="Copy email to clipboard"
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="text-emerald-700">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Developer / App Details Card */}
                <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Developer / Application
                      </p>
                      <p className="font-semibold text-slate-900">
                        Jitendra Kumar &bull; Document Scanner
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        {/* Footer Bar */}
        <footer className="mt-12 border-t border-slate-200 pt-8 text-center text-sm text-slate-500 print:hidden">
          <p>
            &copy; {new Date().getFullYear()} Document Scanner. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/" className="text-teal-700 hover:underline">
              Home
            </Link>
            <span>&bull;</span>
            <a href="#information-collection" className="text-teal-700 hover:underline">
              Back to Top
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}
